import { buildTenantPath, type EnvironmentKey } from '@web-config-app/core';
import { useConfigApp } from '../use-config-app/use-config-app';
import { useConfigAppParams } from '../use-config-app-params/use-config-app-params';

interface ConfigAppPathParams {
  tenantId?: string;
  tabId?: string;
  moduleId?: string;
  /** When provided, the module is inside a container */
  childModuleId?: string;
  environmentKey?: string;
}

export type GetEntityDetailsPathForId = (instanceId: string) => string;
export type GetEntityCreatePathForEnv = (envKey: EnvironmentKey) => string;
export type GetEntityDetailsPathForEnvAndId = (
  instanceId: string,
  envKey: EnvironmentKey,
) => string;
export type GetEntityDetailsPathForModuleEnvAndId = (
  pathTabId: string,
  pathModuleId: string | undefined,
  instanceId: string,
  envKey: EnvironmentKey,
  pathChildModuleId?: string,
) => string;
/**
 * Returns a function named `generateConfigPaths` that generates dynamic paths
 * for the module-based routing system.
 *
 * Route structures:
 *   /tenants/:tenantId/:tabId/environments/:environmentKey/...                          (tab is entity)
 *   /tenants/:tenantId/:tabId/:moduleId/environments/:environmentKey/...                (direct child of tab)
 *   /tenants/:tenantId/:tabId/:moduleId/:childModuleId/environments/:environmentKey/... (module inside container)
 *
 * When childModuleId is provided, the module is nested inside a container.
 * When moduleId is omitted, the tab itself is the entity.
 */
export const useConfigAppPath = () => {
  const { defaultLocale } = useConfigApp();
  const routeParams = useConfigAppParams();

  const generateConfigPaths = ({
    tenantId,
    tabId,
    moduleId,
    childModuleId,
    environmentKey,
  }: ConfigAppPathParams = {}) => {
    const effectiveTenantId = tenantId || routeParams.tenantId;
    const effectiveTabId = tabId || routeParams.tabId;
    const effectiveModuleId = moduleId || routeParams.moduleId;
    const tenantPrefix = effectiveTenantId
      ? `/tenants/${effectiveTenantId}`
      : '';
    const localePath = `/locale/${defaultLocale}`;

    // Three nesting levels:
    //   childModuleId provided: /:tabId/:moduleId/:childModuleId  (module inside container)
    //   moduleId provided:      /:tabId/:moduleId                 (direct child of tab)
    //   neither provided:       /:tabId                           (tab is the module)
    let entityBase: string;
    if (childModuleId) {
      entityBase = `${tenantPrefix}/${effectiveTabId}/${effectiveModuleId}/${childModuleId}`;
    } else if (effectiveModuleId) {
      entityBase = `${tenantPrefix}/${effectiveTabId}/${effectiveModuleId}`;
    } else {
      entityBase = `${tenantPrefix}/${effectiveTabId}`;
    }
    const environmentBase = `${entityBase}/environments/${environmentKey}`;

    return {
      tenantRootPath: buildTenantPath(effectiveTenantId),
      getTenantScopedPath: (subpath: string) =>
        buildTenantPath(effectiveTenantId, subpath),
      tabPath: `${tenantPrefix}/${effectiveTabId}`,
      modulePath: effectiveModuleId
        ? `${tenantPrefix}/${effectiveTabId}/${effectiveModuleId}`
        : `${tenantPrefix}/${effectiveTabId}`,
      entityListPath: `${environmentBase}/list`,
      entityCreatePath: `${environmentBase}/create${localePath}`,
      entitySettingsPath: `${environmentBase}/settings`,
      getEntityDetailsPathForId: (instanceId: string) =>
        `${environmentBase}/details/${instanceId}${localePath}`,
      getEntityCreatePathForEnv: (envKey: EnvironmentKey) =>
        `${entityBase}/environments/${envKey}/create${localePath}`,
      getEntityDetailsPathForEnvAndId: (
        instanceId: string,
        envKey: EnvironmentKey,
      ) =>
        `${entityBase}/environments/${envKey}/details/${instanceId}${localePath}`,
      getEntityDetailsPathForLocale: (instanceId: string, localeId: string) =>
        `${environmentBase}/details/${instanceId}/locale/${localeId}`,
      getEntityCreatePathForLocale: (localeId: string) =>
        `${environmentBase}/create/locale/${localeId}`,
      getEntityDetailsPathForModuleEnvAndId: (
        pathTabId: string,
        pathModuleId: string | undefined,
        instanceId: string,
        envKey: EnvironmentKey,
        pathChildModuleId?: string,
      ) => {
        let pathBase: string;
        if (pathChildModuleId) {
          pathBase = `${tenantPrefix}/${pathTabId}/${pathModuleId}/${pathChildModuleId}`;
        } else if (pathModuleId) {
          pathBase = `${tenantPrefix}/${pathTabId}/${pathModuleId}`;
        } else {
          pathBase = `${tenantPrefix}/${pathTabId}`;
        }
        return `${pathBase}/environments/${envKey}/details/${instanceId}${localePath}`;
      },
    };
  };
  return generateConfigPaths;
};
